import type { PblBackendFile, PblLawyerApplicationPayload, PblLawyerProfile } from '@/types/pbl';

import { initialVolunteerForm } from './constants';
import {
  buildPblCvChoiceTextValues,
  inferPblLanguageOtherText,
  inferPblProfessionalQualificationOtherText,
  normalizePblLanguageValues,
  normalizePblLawFirmPositionValue,
  normalizePblProfessionalQualificationValues,
  normalizePblServiceAreaValues,
} from './profileCvValues';
import type { VolunteerFormState } from './types';

const conductCheckFields = [
  'bankrupt_or_insolvent',
  'arrangement_with_creditors',
  'criminal_conviction',
  'professional_misconduct_or_pending_disciplinary',
  'suspended_from_professional_practice',
] as const;

export const requiresConductSupportingDocument = (form: Pick<VolunteerFormState, typeof conductCheckFields[number]>) =>
  conductCheckFields.some((field) => form[field] === true);

export const requiresOtherProfessionalQualificationText = (
  form: Pick<VolunteerFormState, 'other_professional_qualifications'>
) => form.other_professional_qualifications?.includes('other') === true;

const normalizeOptionalCvText = (value?: string) => (value?.trim() ? value : 'N/A');
const trimNamePart = (value?: string) => value?.trim() || '';
const joinNameParts = (parts: Array<string | undefined>, separator: string) =>
  parts.map(trimNamePart).filter(Boolean).join(separator);
const toStringValue = (value: unknown) => (value === undefined || value === null ? '' : String(value));
const hasText = (value: unknown): value is string => typeof value === 'string' && Boolean(value.trim());
const firstText = (...values: unknown[]) => values.find(hasText)?.trim() || '';
const toBooleanValue = (value: unknown) => value === true || value === 1 || value === '1';
const firstBackendFile = (value?: PblBackendFile | PblBackendFile[] | null): PblBackendFile | null => {
  if (!value) return null;
  return Array.isArray(value) ? value[0] || null : value;
};

const getBackendFileId = (file?: PblBackendFile | null) => {
  const id = file?.id ?? file?.file?.id;
  return typeof id === 'number' ? id : undefined;
};

const getBackendFileName = (file?: PblBackendFile | null) =>
  file?.name || file?.file_name || file?.original_name || file?.filename || file?.file?.name || 'Uploaded file';

const getBackendFileUrl = (file?: PblBackendFile | null) =>
  file?.path || file?.download_url || file?.url || file?.href || file?.file?.path || file?.file?.url;

export const buildVolunteerFullNameEn = (
  form: Pick<VolunteerFormState, 'surname_en' | 'given_name_en' | 'full_name_en'>
) => joinNameParts([form.surname_en, form.given_name_en], ' ') || form.full_name_en || '';

export const buildVolunteerFullNameZh = (
  form: Pick<VolunteerFormState, 'surname_zh' | 'given_name_zh' | 'full_name_zh'>
) => joinNameParts([form.surname_zh, form.given_name_zh], '') || form.full_name_zh || '';

export const mapPblLawyerProfileToVolunteerForm = (profile: PblLawyerProfile): VolunteerFormState => {
  const firmAuthorisationFile = firstBackendFile(profile.firm_authorisation_file);
  const conductSupportingDocumentFile = firstBackendFile(
    profile.conduct_supporting_document_file || profile.conduct_supporting_document
  );
  const firmAuthorisationFileId = profile.firm_authorisation_file_id ?? getBackendFileId(firmAuthorisationFile);
  const conductSupportingDocumentFileId =
    profile.conduct_supporting_document_file_id ?? getBackendFileId(conductSupportingDocumentFile);
  const position = normalizePblLawFirmPositionValue(profile.position_in_law_firm);
  const professionalQualifications = normalizePblProfessionalQualificationValues(
    profile.other_professional_qualifications
  );
  const qualificationOtherText = firstText(
    profile.other_professional_qualification_text,
    inferPblProfessionalQualificationOtherText(profile.cv_other_qualifications_en),
    inferPblProfessionalQualificationOtherText(profile.cv_other_qualifications_zh)
  );
  const languages = normalizePblLanguageValues(profile.language_proficiency);
  const languageOtherText = firstText(
    profile.language_other_text,
    inferPblLanguageOtherText(profile.cv_languages_en),
    inferPblLanguageOtherText(profile.cv_languages_zh)
  );
  const serviceAreas = normalizePblServiceAreaValues(profile.service_areas);
  const generatedChoiceText = buildPblCvChoiceTextValues({
    position_in_law_firm: position,
    position_other_text: profile.position_other_text,
    other_professional_qualifications: professionalQualifications,
    other_professional_qualification_text: qualificationOtherText,
    language_proficiency: languages,
    language_other_text: languageOtherText,
    service_areas: serviceAreas,
  });

  return {
    ...initialVolunteerForm,
    ...profile,
    surname_en: profile.surname_en || '',
    given_name_en: profile.given_name_en || '',
    surname_zh: profile.surname_zh || '',
    given_name_zh: profile.given_name_zh || '',
    full_name_en: buildVolunteerFullNameEn(profile),
    full_name_zh: buildVolunteerFullNameZh(profile),
    phone: profile.phone || '',
    phone_otp: '',
    phone_otp_verified: true,
    email: profile.email || '',
    email_otp: '',
    email_otp_verified: true,
    password: '',
    password_confirmation: '',
    hk_solicitor_admission_year: toStringValue(profile.hk_solicitor_admission_year),
    position_in_law_firm: position,
    position_other_text: firstText(profile.position_other_text, position === 'other' ? profile.cv_position_en : ''),
    other_professional_qualifications: professionalQualifications,
    other_professional_qualification_text: qualificationOtherText,
    language_proficiency: languages,
    language_other_text: languageOtherText,
    service_areas: serviceAreas,
    cv_position_en: firstText(profile.cv_position_en, generatedChoiceText.cv_position_en),
    cv_position_zh: firstText(profile.cv_position_zh, generatedChoiceText.cv_position_zh),
    cv_other_qualifications_en: firstText(
      profile.cv_other_qualifications_en,
      generatedChoiceText.cv_other_qualifications_en
    ),
    cv_other_qualifications_zh: firstText(
      profile.cv_other_qualifications_zh,
      generatedChoiceText.cv_other_qualifications_zh
    ),
    cv_languages_en: firstText(profile.cv_languages_en, generatedChoiceText.cv_languages_en),
    cv_languages_zh: firstText(profile.cv_languages_zh, generatedChoiceText.cv_languages_zh),
    cv_primary_practice_areas_en: firstText(
      profile.cv_primary_practice_areas_en,
      generatedChoiceText.cv_primary_practice_areas_en
    ),
    cv_primary_practice_areas_zh: firstText(
      profile.cv_primary_practice_areas_zh,
      generatedChoiceText.cv_primary_practice_areas_zh
    ),
    cv_publication_consent: toBooleanValue(profile.cv_publication_consent),
    firm_authorisation_file_id: firmAuthorisationFileId,
    firm_authorisation_file: firmAuthorisationFileId
      ? {
          file_id: firmAuthorisationFileId,
          name: getBackendFileName(firmAuthorisationFile),
          url: getBackendFileUrl(firmAuthorisationFile),
        }
      : null,
    conduct_supporting_document_file_id: conductSupportingDocumentFileId,
    conduct_supporting_document_file: conductSupportingDocumentFileId
      ? {
          file_id: conductSupportingDocumentFileId,
          name: getBackendFileName(conductSupportingDocumentFile),
          url: getBackendFileUrl(conductSupportingDocumentFile),
        }
      : null,
    firm_authorisation_confirmed: Boolean(profile.firm_authorisation_confirmed),
    firm_authorisation_letter_attached: Boolean(profile.firm_authorisation_letter_attached),
    law_firm_change_notify_agreed: Boolean(profile.law_firm_change_notify_agreed),
    service_pledge_agreed: Boolean(profile.service_pledge_agreed),
    post_completion_agreed: Boolean(profile.post_completion_agreed),
    pics_agreed: Boolean(profile.pics_agreed),
    declaration_signature: profile.declaration_signature || '',
    declaration_signed_at: profile.declaration_signed_at || '',
    review_confirmed: true,
  };
};

export const buildVolunteerApplicationPayload = (form: VolunteerFormState): PblLawyerApplicationPayload => {
  const {
    cv_signature,
    cv_signed_at,
    firm_authorisation_file,
    conduct_supporting_document_file,
    phone_otp,
    phone_otp_verified,
    email_otp,
    email_otp_verified,
    ...payloadForm
  } = form;

  return {
    ...payloadForm,
    email_verification_code: form.email_otp,
    full_name_en: buildVolunteerFullNameEn(form),
    full_name_zh: buildVolunteerFullNameZh(form),
    hk_solicitor_admission_year: Number(form.hk_solicitor_admission_year),
    cv_pro_bono_experience_en: normalizeOptionalCvText(form.cv_pro_bono_experience_en),
    cv_pro_bono_experience_zh: normalizeOptionalCvText(form.cv_pro_bono_experience_zh),
  };
};

const appendPayloadValue = (formData: FormData, key: string, value: unknown) => {
  if (value === undefined || value === null) return;

  if (Array.isArray(value)) {
    value.forEach((item) => appendPayloadValue(formData, `${key}[]`, item));
    return;
  }

  if (typeof value === 'boolean') {
    formData.append(key, value ? '1' : '0');
    return;
  }

  formData.append(key, String(value));
};

type VolunteerApplicationFormDataOptions = {
  includePassword?: boolean;
  includeAccountFields?: boolean;
};

const accountFieldKeys = new Set([
  'phone',
  'email',
  'password',
  'password_confirmation',
  'email_verification_token',
  'email_verification_code',
  'phone_verification_token',
]);
const passwordFieldKeys = new Set(['password', 'password_confirmation']);

export const buildVolunteerApplicationFormData = (
  form: VolunteerFormState,
  options: VolunteerApplicationFormDataOptions = {}
) => {
  const payload = buildVolunteerApplicationPayload(form);
  const formData = new FormData();
  const includePassword = options.includePassword !== false;
  const includeAccountFields = options.includeAccountFields !== false;

  Object.entries(payload).forEach(([key, value]) => {
    if (!includeAccountFields && accountFieldKeys.has(key)) return;
    if (!includePassword && passwordFieldKeys.has(key)) return;
    appendPayloadValue(formData, key, value);
  });

  return formData;
};
